import apiClient from './client';

export interface OutgoingPaymentDocument {
  id: string;
  fileName: string;
  documentType: string;
  fileSize: number;
  createdAt: string;
}

export interface Payment {
  id: string;
  clientId: string;
  fromAccountId: string;
  fromAccountCurrency: string;
  fromAccountNumber: string;
  fromAccountIban?: string | null;
  /** Payer profile: company name or full name */
  clientDisplayName?: string | null;
  clientPublicId?: string | null;
  clientEmail?: string | null;
  /** Friendly account label from client account (bank account name) */
  clientAccountBankName?: string | null;
  /** Provider (bank) of the source client account */
  clientAccountProviderName?: string | null;
  providerName?: string | null;
  amount: number;
  paymentDate?: string | null;
  feeAmountPreview?: number | null;
  feeAmountFinal?: number | null;
  feeAmountOriginal?: number | null;
  feeCurrencyId?: string | null;
  feeCurrencyMode?: string | null;
  feeConversionRate?: number | null;
  beneficiary: {
    id: string;
    name: string;
    address?: string;
    zipCode?: string;
    city?: string;
    accountNumber: string;
    iban?: string;
    bankName: string;
    swiftCode: string;
    countryId?: string;
    bankCountryId?: string;
    currency: string;
    isActive: boolean;
    correspondentBank?: string | null;
    correspondentSwift?: string | null;
    correspondentCountry?: string | null;
    correspondentAccountNumber?: string | null;
  };
  currency: string;
  purpose: string;
  reference?: string;
  purposeCategory?: number | null;
  status: string;
  rejectionReason?: string;
  failReason?: string | null;
  failedAt?: string | null;
  paymentKind: 'EXTERNAL' | 'INTERNAL' | 'DIGITAL_OUTGOING' | 'DIGITAL_INCOMING';
  paymentMethod?: 'SWIFT' | 'SEPA' | 'INTERNAL';
  ibanVerified?: boolean;
  recipientClientId?: string;
  recipientClientEmail?: string;
  recipientClientPublicId?: string;
  createdAt: string;
  createdByUserId?: string;
  submittedAt?: string;
  sentAt?: string;
  documents: OutgoingPaymentDocument[];
}

export interface RejectPaymentRequest {
  reason: string;
  comment?: string;
}

export interface PutOnHoldRequest {
  reason: string;
  comment?: string;
}

export interface CreateOutgoingDraftRequest {
  fromAccountId: string;
  amount: number;
  paymentDate?: string;
  purpose: string;
  paymentReference: string;
  purposeCategory: number;
  paymentKind?: 'EXTERNAL' | 'INTERNAL' | 'DIGITAL_OUTGOING' | 'DIGITAL_INCOMING';
  recipientClientId?: string;
  beneficiaryName?: string;
  beneficiaryBankName?: string;
  beneficiaryAccountNumber?: string;
  beneficiaryIban?: string;
  beneficiarySwift?: string;
  countryId?: string;
  beneficiaryBankCountryId?: string;
  beneficiaryZipCode?: string;
  beneficiaryCity?: string;
  beneficiaryAddress?: string;
}

export const paymentsApi = {
  createDraft: (clientId: string, data: CreateOutgoingDraftRequest) =>
    apiClient.post<{ id: string }>('/Payment/client/outgoing-payments/draft', data, { params: { clientId } }),

  updateDraft: (clientId: string, paymentId: string, data: CreateOutgoingDraftRequest) =>
    apiClient.put<Payment>(`/Payment/client/outgoing-payments/${paymentId}`, data, { params: { clientId } }),

  submitDraft: (clientId: string, paymentId: string) =>
    apiClient.post(`/Payment/client/outgoing-payments/${paymentId}/submit`, null, { params: { clientId } }),

  deleteDraft: (paymentId: string) =>
    apiClient.delete(`/Payment/admin/outgoing-payments/${paymentId}`),

  // Get all outgoing payments
  getAll: () =>
    apiClient.get<Payment[]>('/Payment/admin/outgoing-payments'),

  // Get digital outgoing payments
  getDigitalOutgoing: () =>
    apiClient.get<Payment[]>('/Payment/admin/outgoing-payments?kind=DIGITAL_OUTGOING'),

  // Get digital incoming payments
  getDigitalIncoming: () =>
    apiClient.get<IncomingPayment[]>('/Payment/admin/incoming-payments?kind=DIGITAL_INCOMING'),

  // Get single payment by ID
  getById: (id: string) =>
    apiClient.get<Payment>(`/Payment/admin/outgoing-payments/${id}`),

  downloadConfirmation: (id: string) =>
    apiClient.get<Blob>(`/Payment/admin/outgoing-payments/${id}/confirmation`, {
      responseType: 'blob',
    }),

  // Approve payment
  approve: (id: string) =>
    apiClient.post('/Payment/admin/approve-outgoing-payments', null, { params: { id } }),

  verifyIban: (id: string) =>
    apiClient.post('/Payment/admin/verify-outgoing-payment-iban', null, { params: { id } }),

  // Reject payment
  reject: (id: string, data: RejectPaymentRequest) =>
    apiClient.post('/Payment/admin/reject-outgoing-payments', data, { params: { id } }),

  // Put on hold
  putOnHold: (id: string, data: PutOnHoldRequest) =>
    apiClient.post('/Payment/admin/put-on-hold-outgoing-payments', data, { params: { id } }),

  // Send external payment
  send: (id: string) =>
    apiClient.post('/Payment/admin/send-outgoing-payments', null, { params: { id } }),

  // Send internal payment
  sendInternal: (id: string) =>
    apiClient.post('/Payment/admin/send-internal-outgoing-payments', null, { params: { id } }),

  // Release ON_HOLD payment back to SUBMITTED
  releaseFromHold: (id: string) =>
    apiClient.post('/Payment/admin/release-from-hold', null, { params: { id } }),

  startUnderReview: (id: string) =>
    apiClient.post(`/Payment/admin/outgoing-payments/${id}/under-review`),

  // Back-office cancel: returns principal + fee to the client and marks CANCELLED (distinct from reject)
  cancel: (id: string, data?: { reason?: string }) =>
    apiClient.post(`/Payment/admin/outgoing-payments/${id}/cancel`, data ?? {}),

  complete: (id: string) =>
    apiClient.post(`/Payment/admin/outgoing-payments/${id}/complete`),

  markFailedAfterSent: (id: string, data: { reason: string; recordedByProvider: boolean }) =>
    apiClient.post(`/Payment/admin/outgoing-payments/${id}/mark-failed`, data),

  overrideFee: (id: string, data: { newFeeAmount: number; reason: string }) =>
    apiClient.post(`/Payment/admin/outgoing-payments/${id}/override-fee`, data),

  getLedgerLinks: (id: string) =>
    apiClient.get<PaymentLedgerLinksResponse>(`/Payment/admin/outgoing-payments/${id}/ledger-links`),

  /** S3 presigned URL for admin preview (avoid XHR/CORS). Local storage: null → use blob download. */
  getAdminOutgoingSupportingDocumentOpenLink: (paymentId: string, documentId: string) =>
    apiClient.get<{ presignedUrl: string | null }>(
      `/Payment/admin/outgoing-payments/${paymentId}/documents/${documentId}/link`,
    ),

  downloadAdminOutgoingSupportingDocument: (paymentId: string, documentId: string) =>
    apiClient.get<Blob>(`/Payment/admin/outgoing-payments/${paymentId}/documents/${documentId}`, {
      responseType: 'blob',
    }),
};

export interface LedgerTransactionLink {
  id: string;
  sourceTransactionId: string;
  linkedTransactionId: string;
  linkType: string;
  createdAt: string;
  description?: string;
}

export interface PaymentLedgerLinksResponse {
  paymentId: string;
  links: LedgerTransactionLink[];
  relatedTransactions: Array<{
    id: string;
    operationType: string;
    createdAt: string;
    amount?: number;
    currencyCode?: string;
    description?: string;
  }>;
}

/** External incoming payments (spec §2) */
export interface IncomingPayment {
  id: string;
  providerId: string;
  clientId?: string | null;
  clientAccountId?: string | null;
  paymentMethod?: 'SWIFT' | 'SEPA';
  receivedOnRequisiteId?: string | null;
  methodMismatch?: boolean;
  currencyId: string;
  amount: number;
  paymentDate?: string | null;
  feeAmount: number;
  netCredited: number;
  /** Fee posts as separate FEE ledger tx at CREDITED when true */
  incomingFeeDeferredToRelease?: boolean;
  incomingFeeLedgerTransactionId?: string | null;
  status: string;
  providerReference?: string | null;
  routedToSuspense: boolean;
  registrationLedgerTransactionId?: string | null;
  matchLedgerTransactionId?: string | null;
  releaseLedgerTransactionId?: string | null;
  registeredAt: string;
  releasedAt?: string | null;
  createdByEmployeeId: string;
  releasedByEmployeeId?: string | null;
  backofficeComment?: string | null;
  rejectReason?: string | null;
  underReviewAt?: string | null;
  approvedAt?: string | null;
  approvedByEmployeeId?: string | null;
  createdAt: string;
  updatedAt: string;
  paymentKind?: 'EXTERNAL' | 'INTERNAL' | 'DIGITAL_OUTGOING' | 'DIGITAL_INCOMING';
  isUnmatched?: boolean;
  beneficiaryName?: string | null;
  beneficiaryBankName?: string | null;
  beneficiaryAccountNumber?: string | null;
  beneficiarySwift?: string | null;
  recipientCountryId?: string | null;
  bankCountryId?: string | null;
  beneficiaryCity?: string | null;
  beneficiaryZipCode?: string | null;
  beneficiaryAddress?: string | null;
}

export interface SaveIncomingDraftRequest {
  providerId?: string;
  clientId?: string;
  clientAccountId?: string;
  currencyId?: string;
  amount: number;
  paymentDate?: string;
  providerReference?: string;
  paymentKind?: 'EXTERNAL' | 'INTERNAL' | 'DIGITAL_OUTGOING' | 'DIGITAL_INCOMING';
  backofficeComment?: string;
  clientUnresolved?: boolean;
}

export interface IncomingFeeQuote {
  grossAmount: number;
  feeAmount: number;
  netAmount: number;
  currencyId: string;
}

export interface RegisterIncomingPaymentRequest {
  providerId: string;
  clientId: string;
  clientAccountId: string;
  currencyId: string;
  amount: number;
  paymentDate?: string;
  providerReference?: string | null;
  paymentKind?: 'EXTERNAL' | 'INTERNAL' | 'DIGITAL_OUTGOING' | 'DIGITAL_INCOMING';
  backofficeComment?: string | null;
}

export interface RegisterUnmatchedIncomingPaymentRequest {
  providerId: string;
  currencyId: string;
  amount: number;
  paymentDate?: string;
  providerReference?: string | null;
  paymentKind?: 'EXTERNAL' | 'INTERNAL' | 'DIGITAL_OUTGOING' | 'DIGITAL_INCOMING';
  backofficeComment?: string | null;
}

export interface MatchIncomingPaymentRequest {
  clientId: string;
  clientAccountId: string;
  /** Required audit trail (spec §2.5) */
  reason: string;
}

export interface RejectIncomingBody {
  reason: string;
}

export interface RegisterIncomingProviderReturnRequest {
  providerId: string;
  currencyId: string;
  amount: number;
  clientAccountId?: string | null;
  originalOutgoingPaymentId?: string | null;
  providerReference?: string | null;
  backofficeComment?: string | null;
}

export interface IncomingProviderReturn {
  id: string;
  providerId: string;
  currencyId: string;
  amount: number;
  receivedAmount: number;
  returnFeeAmount: number;
  creditedAmount: number;
  originalPaymentAmount: number;
  differenceAmount: number;
  valueDate: string;
  originalOutgoingPaymentId?: string | null;
  clientId?: string | null;
  clientAccountId?: string | null;
  routedToSuspense: boolean;
  feeApplied: boolean;
  feeDeferred: boolean;
  feeSkipReason?: string | null;
  isOverridden: boolean;
  overrideReason?: string | null;
  creditedAt?: string | null;
  rejectedAt?: string | null;
  rejectionReason?: string | null;
  status: string;
  providerReference?: string | null;
  registrationLedgerTransactionId?: string | null;
  backofficeComment?: string | null;
  createdByEmployeeId: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePaymentReturnDraftRequest {
  originalOutgoingPaymentId: string;
  receivedAmount: number;
  valueDate: string;
  providerReference?: string;
  comment?: string;
  idempotencyKey?: string;
}

export interface UpdatePaymentReturnDraftRequest {
  receivedAmount: number;
  valueDate: string;
  providerReference?: string;
  comment?: string;
}

export interface PaymentReturnListRequest {
  status?: string;
  providerId?: string;
  currencyId?: string;
  clientId?: string;
  valueDateFrom?: string;
  valueDateTo?: string;
  hasDifference?: boolean;
  limit?: number;
}

export interface PaymentReturnDocument {
  id: string;
  fileName: string;
  documentType: string;
  fileSize: number;
  contentType?: string | null;
  createdAt: string;
}

export interface LedgerMovementRow {
  transactionId: string;
  transactionCreatedAt: string;
  operationType: string;
  businessObjectType: string;
  businessObjectId?: string | null;
  providerId?: string | null;
  entryId: string;
  accountId: string;
  entrySide: string;
  amount: number;
  currencyCode: string;
  purpose: string;
}

export const incomingPaymentsApi = {
  list: (limit = 500) =>
    apiClient.get<IncomingPayment[]>('/Payment/admin/incoming-payments', { params: { limit } }),

  listUnmatched: (limit = 200) =>
    apiClient.get<IncomingPayment[]>('/Payment/admin/incoming-payments/unmatched', { params: { limit } }),

  getById: (id: string) =>
    apiClient.get<IncomingPayment>(`/Payment/admin/incoming-payments/${id}`),

  register: (data: RegisterIncomingPaymentRequest) =>
    apiClient.post<IncomingPayment>('/Payment/admin/incoming-payments/register', data),

  registerUnmatched: (data: RegisterUnmatchedIncomingPaymentRequest) =>
    apiClient.post<IncomingPayment>('/Payment/admin/incoming-payments/register-unmatched', data),

  match: (id: string, data: MatchIncomingPaymentRequest) =>
    apiClient.post<IncomingPayment>(`/Payment/admin/incoming-payments/${id}/match`, data),

  underReview: (id: string) =>
    apiClient.post<IncomingPayment>(`/Payment/admin/incoming-payments/${id}/under-review-incoming`),

  approve: (id: string) =>
    apiClient.post<IncomingPayment>(`/Payment/admin/incoming-payments/${id}/approve`),

  reject: (id: string, data: RejectIncomingBody) =>
    apiClient.post<IncomingPayment>(`/Payment/admin/incoming-payments/${id}/reject`, data),

  releaseToAvailable: (id: string) =>
    apiClient.post<IncomingPayment>(`/Payment/admin/incoming-payments/${id}/release-to-available`),

  createDraft: (data: SaveIncomingDraftRequest) =>
    apiClient.post<IncomingPayment>('/Payment/admin/incoming-payments/draft', data),

  updateDraft: (id: string, data: SaveIncomingDraftRequest) =>
    apiClient.put<IncomingPayment>(`/Payment/admin/incoming-payments/${id}/draft`, data),

  submitDraft: (id: string) =>
    apiClient.post<IncomingPayment>(`/Payment/admin/incoming-payments/${id}/submit`),

  deleteDraft: (id: string) =>
    apiClient.delete(`/Payment/admin/incoming-payments/${id}/draft`),

  feeQuote: (params: {
    clientId?: string;
    providerId?: string;
    clientAccountId?: string;
    amount: number;
    currencyId?: string;
    paymentKind?: string;
  }) =>
    apiClient.get<IncomingFeeQuote>('/Payment/admin/incoming-payments/fee-quote', { params }),

  overrideFee: (id: string, data: { newFeeAmount: number; reason: string }) =>
    apiClient.post<IncomingPayment>(`/Payment/admin/incoming-payments/${id}/override-fee`, data),
};

export interface OftIncoming {
  id: string;
  providerId: string;
  clientId: string;
  clientAccountId: string;
  currencyId: string;
  amount: number;
  feeAmount: number;
  netCredited: number;
  incomingFeeDeferredToRelease?: boolean;
  incomingFeeLedgerTransactionId?: string | null;
  status: string;
  providerReference?: string | null;
  routedToSuspense: boolean;
  registrationLedgerTransactionId?: string | null;
  matchLedgerTransactionId?: string | null;
  releaseLedgerTransactionId?: string | null;
  registeredAt: string;
  paymentDate?: string | null;
  releasedAt?: string | null;
  createdByEmployeeId: string;
  releasedByEmployeeId?: string | null;
  backofficeComment?: string | null;
  rejectReason?: string | null;
  underReviewAt?: string | null;
  createdAt: string;
  updatedAt: string;
  paymentKind: 'DIGITAL_INCOMING';
}

export interface SaveOftIncomingDraftRequest {
  providerId?: string;
  clientId?: string;
  clientAccountId?: string;
  currencyId?: string;
  amount: number;
  paymentDate?: string;
  providerReference?: string;
  backofficeComment?: string;
}

export const oftIncomingApi = {
  list: (limit = 500) =>
    apiClient.get<OftIncoming[]>('/Payment/admin/oft-incoming', { params: { limit } }),

  getById: (id: string) =>
    apiClient.get<OftIncoming>(`/Payment/admin/oft-incoming/${id}`),

  createDraft: (data: SaveOftIncomingDraftRequest) =>
    apiClient.post<OftIncoming>('/Payment/admin/oft-incoming/draft', data),

  updateDraft: (id: string, data: SaveOftIncomingDraftRequest) =>
    apiClient.put<OftIncoming>(`/Payment/admin/oft-incoming/${id}/draft`, data),

  submitDraft: (id: string) =>
    apiClient.post<OftIncoming>(`/Payment/admin/oft-incoming/${id}/submit`),

  deleteDraft: (id: string) =>
    apiClient.delete(`/Payment/admin/oft-incoming/${id}/draft`),

  feeQuote: (params: {
    clientId?: string;
    providerId?: string;
    clientAccountId?: string;
    amount: number;
    currencyId?: string;
  }) =>
    apiClient.get<IncomingFeeQuote>('/Payment/admin/oft-incoming/fee-quote', { params }),

  overrideFee: (id: string, data: { newFeeAmount: number; reason: string }) =>
    apiClient.post<OftIncoming>(`/Payment/admin/oft-incoming/${id}/override-fee`, data),

  underReview: (id: string) =>
    apiClient.post<OftIncoming>(`/Payment/admin/oft-incoming/${id}/under-review-incoming`),

  approve: (id: string) =>
    apiClient.post<OftIncoming>(`/Payment/admin/oft-incoming/${id}/approve`),

  reject: (id: string, data: RejectIncomingBody) =>
    apiClient.post<OftIncoming>(`/Payment/admin/oft-incoming/${id}/reject`, data),

  releaseToAvailable: (id: string) =>
    apiClient.post<OftIncoming>(`/Payment/admin/oft-incoming/${id}/release-to-available`),
};

export const incomingReturnsApi = {
  list: (limit = 200) =>
    apiClient.get<IncomingProviderReturn[]>('/Payment/admin/incoming-returns', { params: { limit } }),
  register: (data: RegisterIncomingProviderReturnRequest) =>
    apiClient.post<IncomingProviderReturn>('/Payment/admin/incoming-returns/register', data),
};

export const paymentReturnsApi = {
  list: (params: PaymentReturnListRequest = {}) =>
    apiClient.get<IncomingProviderReturn[]>('/Payment/admin/payment-returns', { params }),
  getById: (id: string) =>
    apiClient.get<IncomingProviderReturn>(`/Payment/admin/payment-returns/${id}`),
  createDraft: (data: CreatePaymentReturnDraftRequest) =>
    apiClient.post<IncomingProviderReturn>('/Payment/admin/payment-returns/draft', data),
  updateDraft: (id: string, data: UpdatePaymentReturnDraftRequest) =>
    apiClient.put<IncomingProviderReturn>(`/Payment/admin/payment-returns/${id}/draft`, data),
  deleteDraft: (id: string, reason: string) =>
    apiClient.delete(`/Payment/admin/payment-returns/${id}/draft`, { data: { reason } }),
  confirm: (id: string) =>
    apiClient.post<IncomingProviderReturn>(`/Payment/admin/payment-returns/${id}/confirm`),
  creditFromSuspense: (id: string) =>
    apiClient.post<IncomingProviderReturn>(`/Payment/admin/payment-returns/${id}/credit-from-suspense`),
  reject: (id: string, reason: string) =>
    apiClient.post<IncomingProviderReturn>(`/Payment/admin/payment-returns/${id}/reject`, { reason }),
  overrideFee: (id: string, finalFeeAmount: number, reason: string) =>
    apiClient.post<IncomingProviderReturn>(`/Payment/admin/payment-returns/${id}/override-fee`, { finalFeeAmount, reason }),
  listDocuments: (id: string) =>
    apiClient.get<PaymentReturnDocument[]>(`/Payment/admin/payment-returns/${id}/documents`),
  uploadDocument: (id: string, file: File, documentType = 'SUPPORTING') => {
    const data = new FormData();
    data.append('file', file);
    data.append('documentType', documentType);
    return apiClient.post<PaymentReturnDocument>(`/Payment/admin/payment-returns/${id}/documents`, data);
  },
  downloadDocument: (id: string, documentId: string) =>
    apiClient.get<Blob>(`/Payment/admin/payment-returns/${id}/documents/${documentId}`, { responseType: 'blob' }),
};

export const ledgerReportingApi = {
  movements: (limit = 500) =>
    apiClient.get<LedgerMovementRow[]>('/Payment/admin/ledger-movements', { params: { limit } }),
};
